import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useApolloClient } from '@apollo/client';
import { useMutationWithToast } from '@pesto/ui';
import { Typography } from '@pesto/ui/components/Typography';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../../types/modal.types';

import {
  type AddLoanMemberMutationMutationVariables,
  useAddLoanMemberMutationMutation,
} from '../../../__generated__/graphql/api';
import { LoanMemberPickerField } from '../../../components/UserPicker/LoanMemberPickerField';
import { getOnboardMutationRefreshOptions } from '../../../utils/userCacheInvalidation';
import { memberWasAdded } from './addLoanMemberOutcome';

const addLoanMemberSchema = z
  .object({
    member: z
      .object({ id: z.string().nullish() })
      .passthrough()
      .nullish()
      .refine((member) => !!member?.id, 'Pick the person joining this loan'),
    card_ref: z.string().optional(),
    last_four: z.string().max(4, 'Last four must be 4 digits').optional(),
  })
  .superRefine((data, ctx) => {
    const cardRef = data.card_ref?.trim();
    const lastFour = data.last_four?.trim() ?? '';

    if (lastFour && !cardRef) {
      ctx.addIssue({ code: 'custom', path: ['card_ref'], message: 'Card reference is required with last four' });
    }

    if (cardRef && !/^\d{4}$/.test(lastFour)) {
      ctx.addIssue({ code: 'custom', path: ['last_four'], message: 'Last four is required (4 digits)' });
    }
  });

type AddLoanMemberFormData = z.infer<typeof addLoanMemberSchema>;

const formConfig: SmartFormConfig<AddLoanMemberFormData> = {
  fieldGroups: [
    {
      name: 'person',
      fields: [
        {
          name: 'member',
          label: 'Person',
          component: LoanMemberPickerField,
          required: true,
          defaultValue: null,
        },
      ],
    },
    {
      name: 'card',
      label: 'Card',
      description: 'Optional — leave both blank to add the member without a card. A reference needs its last four.',
      fields: [
        {
          name: 'card_ref',
          label: 'Card Reference',
          type: 'text',
          defaultValue: '',
        },
        {
          name: 'last_four',
          label: 'Last Four',
          type: 'text',
          defaultValue: '',
        },
      ],
    },
  ],
  schema: addLoanMemberSchema,
  defaultValues: { member: null, card_ref: '', last_four: '' },
  layout: 'grid',
  columns: 2,
};

export const AddLoanMemberModalComponent = ({ payload, api }: ModalProps<LoanModalPayload, LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const client = useApolloClient();
  const [addLoanMember, { loading }] = useAddLoanMemberMutationMutation(getOnboardMutationRefreshOptions());

  const handleSubmit = async (data: AddLoanMemberFormData) => {
    const cardRef = data.card_ref?.trim();
    const variables: AddLoanMemberMutationMutationVariables = { loanId, userId: data.member?.id ?? '' };

    if (cardRef) {
      variables.cardRef = cardRef;
      variables.lastFour = data.last_four?.trim();
    }

    await handleMutation({
      mutation: () => addLoanMember({ variables }),
      extractSuccess: (result) => !!result?.data?.addLoanMember?.id,
      onSuccess: () => api.close({ loanId }),
      onError: (error) => {
        if (memberWasAdded(error.message)) void client.refetchQueries({ include: 'active' });
      },
      debugLabel: 'add-loan-member-modal',
      toast: {
        success: 'Member added to loan',
        loading: 'Adding member...',
        errorToast: (error) =>
          memberWasAdded(error.message)
            ? {
                title: 'Member added — card incomplete',
                description: error.message,
                duration: 12000,
              }
            : { title: 'Failed to add member', description: error.message, duration: 6000 },
      },
    });
  };

  return (
    <SmartForm config={formConfig} onSubmit={handleSubmit} loading={loading} submitLabel="Add Member">
      {() => (
        <Typography variant="bodySmall" className="text-muted-foreground">
          Members join under the identity they already have. Create the person first if starlink does not know them yet.
        </Typography>
      )}
    </SmartForm>
  );
};
